// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/pages/PackingDispatchDetailPage.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  useParams
} from "react-router-dom";

import {
  Plus
} from "lucide-react";

import {
  getDispatchDetail,
  submitDispatch
} from "../services/materialDispatchService";

import AddDispatchLineDrawer
from "../components/AddDispatchLineDrawer";

import ApproveDispatchDialog
from "../components/ApproveDispatchDialog";

// =============================================================================
// COMPONENT
// =============================================================================

export default function PackingDispatchDetailPage() {

  // ===========================================================================
  // PARAMS
  // ===========================================================================
  const {
    dispatchId
  } = useParams();


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [detail, setDetail] =
    useState(null);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [submitLoading, setSubmitLoading] =
    useState(false);
  
  const [approveOpen, setApproveOpen] =
  useState(false);



  // ===========================================================================
  // LOAD DETAIL
  // ===========================================================================
  const loadDetail = async () => {

    try {

      setLoading(true);

      setError("");

      const response =
        await getDispatchDetail(
          dispatchId
        );

      if (response?.success) {

        setDetail(
          response.data
        );

      } else {

        setError(
          "Failed to load dispatch detail"
        );
      }

    } catch (err) {

      console.error(err);

      setError(
        "Failed to load dispatch detail"
      );

    } finally {

      setLoading(false);
    }
  };


  // ===========================================================================
  // HANDLE SUBMIT
  // ===========================================================================
  const handleSubmit = async () => {

    try {

      const confirmed =
        window.confirm(

          "Are you sure you want to submit this dispatch?"
        );

      if (!confirmed) {

        return;
      }

      setSubmitLoading(true);

      const response =
        await submitDispatch(
          dispatchId
        );

      if (response?.success) {

        alert(
          "Dispatch submitted successfully"
        );

        loadDetail();
      }

    } catch (err) {

      console.error(err);

      alert(

        err?.response?.data?.message ||

        "Failed to submit dispatch"
      );

    } finally {

      setSubmitLoading(false);
    }
  };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

    loadDetail();

  }, [dispatchId]);


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>
        Loading dispatch detail...
      </div>
    );
  }


  // ===========================================================================
  // ERROR
  // ===========================================================================
  if (error) {

    return (

      <div style={styles.errorBox}>
        {error}
      </div>
    );
  }


  // ===========================================================================
  // HEADER
  // ===========================================================================
  const header =
    detail?.header || {};

  const lines =
    detail?.lines || [];


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerCard}>


        <div>

          <div style={styles.title}>

            {

              header.packing_list_no ||

              "Packing Dispatch Detail"
            }

          </div>

          <div style={styles.subTitle}>

            Project:
            {" "}

            {

              header.project_name ||

              "-"
            }

          </div>

        </div>


        <div style={styles.headerRight}>


  <span style={
    getStatusStyle(
      header.dispatch_status
    )
  }>

    {

      header.dispatch_status ||

      "DRAFT"
    }

  </span>


  {/* ============================================================ */}
  {/* DRAFT ACTIONS */}
  {/* ============================================================ */}
  {

    header.dispatch_status ===
    "DRAFT" && (

      <>

        <button
          style={styles.addBtn}
          onClick={() =>
            setDrawerOpen(true)
          }
        >

          <Plus size={16} />

          Add Line

        </button>


        <button
          style={styles.submitBtn}
          onClick={handleSubmit}
          disabled={submitLoading}
        >

          {

            submitLoading

              ? "Submitting..."

              : "Submit Dispatch"
          }

        </button>

      </>
    )
  }


  {/* ============================================================ */}
  {/* SUBMITTED ACTION */}
  {/* ============================================================ */}
  {

    header.dispatch_status ===
    "SUBMITTED" && (

      <button
        style={styles.approveBtn}
        onClick={() =>
          setApproveOpen(true)
        }
      >

        Approve Dispatch

      </button>
    )
  }

</div>
      </div>


      {/* ================================================================ */}
      {/* SUMMARY */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>
          Dispatch Summary
        </div>


        <div style={styles.summaryGrid}>


          <SummaryItem
            label="Site Store"
            value={
              header.site_store_name
            }
          />

          <SummaryItem
            label="Vehicle No"
            value={
              header.vehicle_no
            }
          />

          <SummaryItem
            label="Driver Name"
            value={
              header.driver_name
            }
          />

          <SummaryItem
            label="Transporter"
            value={
              header.transporter_name
            }
          />

          <SummaryItem
            label="Dispatch Date"
            value={
              header.dispatch_date
            }
          />

          <SummaryItem
            label="Expected Delivery"
            value={
              header.expected_delivery_date
            }
          />

        </div>

      </div>


      {/* ================================================================ */}
      {/* WORKFLOW */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.sectionTitle}>
          Workflow Status
        </div>


        <div style={styles.workflowBar}>


          {

            workflowSteps.map((step) => {

              const active =
                step ===
                header.dispatch_status;

              return (

                <div
                  key={step}
                  style={
                    active
                      ? styles.activeStep
                      : styles.step
                  }
                >

                  {step}

                </div>
              );
            })
          }

        </div>

      </div>


      {/* ================================================================ */}
      {/* DISPATCH LINES */}
      {/* ================================================================ */}
      <div style={styles.sectionCard}>


        <div style={styles.linesHeader}>


          <div style={styles.sectionTitle}>

            Dispatch Lines
            {" "}
            ({lines.length})

          </div>

        </div>


        {

          lines.length === 0 ? (

            <div style={styles.placeholder}>

              No dispatch lines added yet

            </div>

          ) : (

            <div style={styles.lineTable}>


              {/* ======================================================== */}
              {/* TABLE HEADER */}
              {/* ======================================================== */}
              <div style={styles.tableHeader}>


                <div style={styles.colMaterial}>
                  Material
                </div>

                <div style={styles.colCode}>
                  Code
                </div>

                <div style={styles.colQty}>
                  Qty
                </div>

                <div style={styles.colBox}>
                  Box
                </div>

                <div style={styles.colWeight}>
                  Weight
                </div>

              </div>


              {/* ======================================================== */}
              {/* TABLE ROWS */}
              {/* ======================================================== */}
              {

                lines.map((line) => (

                  <div
                    key={line.id}
                    style={styles.lineRow}
                  >


                    {/* ------------------------------------------------- */}
                    {/* MATERIAL */}
                    {/* ------------------------------------------------- */}
                    <div style={styles.colMaterial}>


                      <div style={styles.materialName}>

                        {

                          line.material_name ||

                          line.material_description ||

                          "-"
                        }

                      </div>


                      <div style={styles.materialSub}>

                        Reservation:
                        {" "}

                        {

                          line.reservation_id || "-"
                        }

                      </div>

                    </div>


                    {/* ------------------------------------------------- */}
                    {/* CODE */}
                    {/* ------------------------------------------------- */}
                    <div style={styles.colCode}>

                      {

                        line.material_code || "-"
                      }

                    </div>


                    {/* ------------------------------------------------- */}
                    {/* QTY */}
                    {/* ------------------------------------------------- */}
                    <div style={styles.colQty}>

                      {

                        line.dispatch_qty || 0
                      }

                    </div>


                    {/* ------------------------------------------------- */}
                    {/* BOX */}
                    {/* ------------------------------------------------- */}
                    <div style={styles.colBox}>

                      {

                        line.box_no || "-"
                      }

                    </div>


                    {/* ------------------------------------------------- */}
                    {/* WEIGHT */}
                    {/* ------------------------------------------------- */}
                    <div style={styles.colWeight}>

                      {

                        line.weight || 0
                      }

                    </div>

                  </div>
                ))
              }

            </div>
          )
        }

      </div>


      {/* ================================================================ */}
      {/* ADD LINE DRAWER */}
      {/* ================================================================ */}
      <AddDispatchLineDrawer

        open={drawerOpen}

        dispatchId={dispatchId}

        projectId={header.project_id}

        onClose={() =>
          setDrawerOpen(false)
        }

        onSuccess={() => {

          setDrawerOpen(false);

          loadDetail();
        }}
      />

      <ApproveDispatchDialog

  open={approveOpen}

  dispatchId={dispatchId}

  onClose={() =>
    setApproveOpen(false)
  }

  onSuccess={() => {

    setApproveOpen(false);

    loadDetail();
  }}
/>

    </div>
  );
}


// =============================================================================
// SUMMARY ITEM
// =============================================================================

function SummaryItem({

  label,

  value

}) {

  return (

    <div style={styles.summaryItem}>

      <div style={styles.summaryLabel}>
        {label}
      </div>

      <div style={styles.summaryValue}>
        {value || "-"}
      </div>

    </div>
  );
}


// =============================================================================
// WORKFLOW STEPS
// =============================================================================

const workflowSteps = [

  "DRAFT",

  "SUBMITTED",

  "APPROVED",

  "IN_TRANSIT",

  "RECEIVED",

  "ACCEPTED"
];


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "APPROVED":

      return styles.statusGreen;

    case "SUBMITTED":

      return styles.statusBlue;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  page: {

    padding: 20,

    background: "#f5f7fb",

    minHeight: "100vh"
  },

  loader: {

    padding: 40,

    textAlign: "center"
  },

  errorBox: {

    margin: 20,

    padding: 20,

    background: "#fee2e2",

    color: "#b91c1c",

    borderRadius: 12
  },

  headerCard: {

    background:
      "linear-gradient(90deg, #2563eb, #3b82f6)",

    borderRadius: 14,

    padding: 20,

    marginBottom: 20,

    color: "#ffffff",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  headerRight: {

    display: "flex",

    alignItems: "center",

    gap: 12
  },

  addBtn: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    background: "#111827",

    color: "#ffffff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    cursor: "pointer",

    fontWeight: 600
  },

  submitBtn: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    background: "#16a34a",

    color: "#ffffff",

    border: "none",

    borderRadius: 8,

    padding: "10px 16px",

    cursor: "pointer",

    fontWeight: 600
  },

  title: {

    fontSize: 24,

    fontWeight: 700
  },

  subTitle: {

    marginTop: 6,

    fontSize: 13,

    opacity: 0.9
  },

  sectionCard: {

    background: "#ffffff",

    borderRadius: 12,

    padding: 20,

    marginBottom: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  sectionTitle: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827"
  },

  linesHeader: {

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    marginBottom: 16
  },

  summaryGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(auto-fit, minmax(220px, 1fr))",

    gap: 16
  },

  summaryItem: {

    background: "#f9fafb",

    borderRadius: 10,

    padding: 14
  },

  summaryLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginBottom: 6
  },

  summaryValue: {

    fontSize: 14,

    fontWeight: 600,

    color: "#111827"
  },

  workflowBar: {

    display: "flex",

    gap: 12,

    flexWrap: "wrap"
  },

  activeStep: {

    background: "#2563eb",

    color: "#ffffff",

    padding: "10px 16px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  step: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "10px 16px",

    borderRadius: 30,

    fontSize: 12,

    fontWeight: 700
  },

  placeholder: {

    padding: 40,

    textAlign: "center",

    color: "#6b7280",

    border:
      "1px dashed #d1d5db",

    borderRadius: 10
  },

  lineTable: {

    display: "flex",

    flexDirection: "column",

    gap: 10
  },

  tableHeader: {

    display: "grid",

    gridTemplateColumns:
      "2.5fr 1.5fr 1fr 1fr 1fr",

    gap: 16,

    padding: "14px 16px",

    background: "#f9fafb",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    fontSize: 13,

    fontWeight: 700,

    color: "#374151",

    marginBottom: 10
  },

  lineRow: {

    display: "grid",

    gridTemplateColumns:
      "2.5fr 1.5fr 1fr 1fr 1fr",

    gap: 16,

    padding: "14px 16px",

    border:
      "1px solid #e5e7eb",

    borderRadius: 10,

    alignItems: "center",

    transition:
      "all 0.2s ease",

    background: "#ffffff"
  },

  colMaterial: {

    display: "flex",

    flexDirection: "column",

    justifyContent: "center"
  },

  colCode: {

    display: "flex",

    alignItems: "center"
  },

  colQty: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    fontWeight: 600
  },

  colBox: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center"
  },

  colWeight: {

    display: "flex",

    alignItems: "center",

    justifyContent: "center"
  },

  materialName: {

    fontWeight: 600,

    color: "#111827"
  },

  materialSub: {

    marginTop: 4,

    fontSize: 12,

    color: "#6b7280"
  },

  statusGray: {

    background: "#f3f4f6",

    color: "#374151",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#15803d",

    padding: "8px 14px",

    borderRadius: 20,

    fontSize: 12,

    fontWeight: 700
  },
  approveBtn: {

  display: "flex",

  alignItems: "center",

  gap: 8,

  background: "#7c3aed",

  color: "#ffffff",

  border: "none",

  borderRadius: 8,

  padding: "10px 16px",

  cursor: "pointer",

  fontWeight: 600
},
};